import React from 'react';
import classnames from 'classnames';
import { isEmpty } from 'lodash';
import { activeConsultationPackages } from '@fiverr-private/consultation';
import { getContext } from '@fiverr-private/fiverr_context';
import { I18n } from '@fiverr-private/i18n-react';
import { Avatar } from '@fiverr-private/fit';
import { Container, Stack } from '@fiverr-private/layout_components';
import { SOURCES } from '../../../utils/monitoring/monitoring-constants';
import { ProUserInfoContainer } from '../../UserInfoContainer/ProUserInfoContainer';
import { HourlyRate } from '../../HourlyRate';
import { ContactButton } from '../../ProActionButtons/ContactButton';
import { RespondTimeInfo } from '../../RespondTimeInfo';
import { useSellerVacation } from '../../../hooks/useSellerVacation';
import { ConsultationButton } from '../../ConsultationButton';
import { useConsultationData } from '../../../hooks/useConsultationData';
import { FederatedSeller } from '../../../types';
import { GigData } from '../../../server/types/GigData';
import styles from './index.module.scss';

const T_PREFIX = 'seller_pages_toolbox.pro_contact';

export interface ContactCardProps {
    className?: string;
    timeZone: string | null;
    isOnline: boolean;
    withTitle?: boolean;
    newView?: boolean;
    seller: FederatedSeller;
    buttonCopy?: React.ReactNode;
    renderTrigger?: () => React.ReactNode;
    fullHeight?: boolean;
    aiBriefInitialGenerationText?: string;
    gigs: GigData[];
}

export const ProContactCard = ({
    className,
    withTitle = true,
    timeZone,
    isOnline,
    newView,
    seller,
    buttonCopy,
    renderTrigger,
    fullHeight,
    gigs,
    aiBriefInitialGenerationText,
}: ContactCardProps) => {
    const { isMobile } = getContext();
    const { isOnVacation } = useSellerVacation();
    const consultationData = useConsultationData();
    const hasConsultation = !isEmpty(consultationData) && !!activeConsultationPackages(consultationData.pricing).length;

    const user = seller?.user;
    const username = user?.name || '';
    const displayName = user?.profile?.displayName || username;

    return (
        <div className={classnames(className, styles.contactCard, 'p-32 box-shadow-z1')}>
            {newView && (
                <div className={styles.avatarBox}>
                    <div className="m-r-16">
                        <Avatar
                            size={48}
                            username={user?.name || username}
                            src={user?.profileImageUrl || ''}
                            isOnline={isOnline}
                            showOnlineIndicator={isOnline}
                            onlineIndicatorSize={8}
                            onlineIndicatorPosition={(Avatar as any).ONLINE_INDICATOR_POSITIONS.BOTTOM_RIGHT}
                        />
                    </div>
                    <Container width="100%">
                        <Stack direction="column" width="100%" className="text-display-6 m-b-8">
                            <Stack justifyContent="spaceBetween" width="100%">
                                <span>{displayName}</span>
                                <HourlyRate hourlyRateInUsdCents={seller?.hourlyRate?.priceInCents || 0} />
                            </Stack>
                        </Stack>
                        <ProUserInfoContainer timeZone={timeZone || ''} isOnline={isOnline} withTooltip />
                    </Container>
                </div>
            )}
            {!newView && withTitle && !isOnVacation && (
                <div className="flex flex-center co-text-darker text-semi-bold">
                    <I18n k={`${T_PREFIX}.title`} />
                </div>
            )}

            <ContactButton
                className={classnames(styles.contactButton, 'm-t-20 m-b-16')}
                size="lg"
                source={SOURCES.page}
                shouldRenderFloatingBtn={!isMobile}
                buttonCopy={buttonCopy}
                fullHeight={fullHeight}
                aiBriefInitialGenerationText={aiBriefInitialGenerationText}
                renderTrigger={renderTrigger}
                seller={seller as FederatedSeller}
                isOnline={isOnline}
                gigs={gigs}
                disableProMigration
            />

            {hasConsultation && <ConsultationButton />}

            <RespondTimeInfo />
        </div>
    );
};
